import React from "react";
import { FileText, ShieldCheck, Settings, FileCheck, Layers, CircleCheck as CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { type L6 } from "@/lib/commonCopy";
interface StaticDoc {
  id: string;
  code: string;
  category: L6;
  title: L6;
  summary: L6;
  specs: L6;
  detailUrl: string;
}
const STATIC_DOCUMENTS: StaticDoc[] = [{
  id: "doc-1",
  code: "DS-316L-SS",
  category: {
    en: "Product Reference",
    zh: "产品技术参考",
    es: "Referencia de producto",
    de: "Produktreferenz",
    fr: "Référence produit",
    pt: "Referência do produto"
  },
  title: {
    en: "316L Stainless Steel Woven Mesh Technical Specification",
    zh: "316L 不锈钢编织丝网技术规格书",
    es: "Especificación técnica de malla tejida de acero inoxidable 316L",
    de: "Technische Spezifikation für Drahtgewebe aus Edelstahl 316L",
    fr: "Spécification technique de toile tissée en acier inoxydable 316L",
    pt: "Especificação técnica de tela tecida de aço inoxidável 316L"
  },
  summary: {
    en: "Comprehensive wire diameter, open area percentage, micron rating, and mechanical strength parameters for 316L Dutch, Twilled, and Plain weave filter meshes.",
    zh: "涵盖 316L 荷兰编织、斜纹编织与平纹编织过滤丝网的丝径公差、开孔率、截留精度及机械拉伸强度参数。",
    es: "Parámetros integrales de diámetro de alambre, porcentaje de área abierta, micras y resistencia mecánica para mallas 316L.",
    de: "Umfassende Kennwerte zu Drahtdurchmesser, offener Siebfläche, Filterfeinheit und Festigkeit für Drahtgewebe 316L.",
    fr: "Paramètres complets de diamètre de fil, surface ouverte, seuil de filtration et résistance mécanique pour toiles 316L.",
    pt: "Parâmetros abrangentes de diâmetro do arame, porcentagem de área aberta, micras e resistência mecânica para telas 316L."
  },
  specs: {
    en: "Aperture Range: 2 µm – 500 µm | ASTM E2016 Compliant",
    zh: "孔径公差范围：2 µm – 500 µm | 符合 ASTM E2016 规范",
    es: "Rango de abertura: 2 µm – 500 µm | Conforme a ASTM E2016",
    de: "Öffnungsbereich: 2 µm – 500 µm | ASTM E2016-konform",
    fr: "Plage d'ouverture : 2 µm – 500 µm | Conforme ASTM E2016",
    pt: "Faixa de abertura: 2 µm – 500 µm | Conforme ASTM E2016"
  },
  detailUrl: "/DatasheetsPage"
}, {
  id: "doc-2",
  code: "STD-ISO-9044",
  category: {
    en: "Compliance & Standards",
    zh: "合规与行业标准",
    es: "Conformidad y normas",
    de: "Konformität & Normen",
    fr: "Conformité et normes",
    pt: "Conformidade e normas"
  },
  title: {
    en: "ISO 9044 & ASTM E2016 Industrial Wire Cloth Standards Guide",
    zh: "ISO 9044 与 ASTM E2016 工业金属丝织网标准指南",
    es: "Guía de normas industriales para telas metálicas ISO 9044 y ASTM E2016",
    de: "Leitfaden zu Industriellen Drahtgewebenormen ISO 9044 & ASTM E2016",
    fr: "Guide des normes de toiles métalliques industrielles ISO 9044 et ASTM E2016",
    pt: "Guia de normas industriais para telas metálicas ISO 9044 e ASTM E2016"
  },
  summary: {
    en: "Quality assurance tolerances, wire diameter variance allowances, aperture measurement techniques, and testing methodology standards.",
    zh: "质量保证允许公差、丝径偏差范围、网孔显微测量技术与检测方法执行标准。",
    es: "Tolerancias de aseguramiento de calidad, tolerancias de diámetro de alambre, técnicas de medición y metodologías de ensayo.",
    de: "Qualitätssicherungstoleranzen, Drahtdurchmesser-Abweichungen, Messverfahren für Maschenweiten und Prüfmethoden.",
    fr: "Tolérances d'assurance qualité, écarts admissibles de diamètre de fil, techniques de mesure d'ouverture et méthodes d'essai.",
    pt: "Tolerâncias de garantia da qualidade, desvios de diâmetro de arame, técnicas de medição de abertura e metodologias de ensaio."
  },
  specs: {
    en: "Verification Grade: ISO Class 1 & Class 2 Standard",
    zh: "检验等级：符合 ISO Class 1 与 Class 2 标准",
    es: "Grado de verificación: Norma ISO Clase 1 y Clase 2",
    de: "Prüfklasse: ISO Klasse 1 & Klasse 2 Standard",
    fr: "Classe de vérification : Norme ISO Classe 1 et Classe 2",
    pt: "Grau de verificação: Norma ISO Classe 1 e Classe 2"
  },
  detailUrl: "/ISOWireMeshStandardsPage"
}, {
  id: "doc-3",
  code: "ENG-CORR-04",
  category: {
    en: "Engineering Data",
    zh: "工程数据矩阵",
    es: "Datos de ingeniería",
    de: "Technische Daten",
    fr: "Données d'ingénierie",
    pt: "Dados de engenharia"
  },
  title: {
    en: "Corrosion Resistance Matrix for Harsh Industrial Media",
    zh: "严苛工业介质耐腐蚀选材矩阵指南",
    es: "Matriz de resistencia a la corrosión para medios industriales agresivos",
    de: "Korrosionsbeständigkeitsmatrix für aggressive Industriemedien",
    fr: "Matrice de résistance à la corrosion pour milieux industriels sévères",
    pt: "Matriz de resistência à corrosão para meios industriais agressivos"
  },
  summary: {
    en: "Chemical compatibility data for 304L, 316L, 904L, Alloy 20, Hastelloy, and Titanium filter meshes in acidic, marine, and high-temperature environments.",
    zh: "提供 304L、316L、904L、Alloy 20、哈氏合金与钛网在酸性、海洋盐雾及高温环境下的化学兼容性评级数据。",
    es: "Datos de compatibilidad química para mallas filtrantes de 304L, 316L, 904L, Alloy 20, Hastelloy y titanio en entornos ácidos y marinos.",
    de: "Chemische Beständigkeitsdaten für Filtergewebe aus 304L, 316L, 904L, Alloy 20, Hastelloy und Titan in sauren und maritimen Umgebungen.",
    fr: "Données de compatibilité chimique pour toiles filtrantes en 304L, 316L, 904L, Alloy 20, Hastelloy et titane en milieux acides et marins.",
    pt: "Dados de compatibilidade química para telas filtrantes de 304L, 316L, 904L, Alloy 20, Hastelloy e titânio em meios ácidos e marinhos."
  },
  specs: {
    en: "Oil & Gas, Chemical & Subsea Process Data",
    zh: "适用油气、化工与海底深水工况选材",
    es: "Datos para procesos de petróleo y gas, químicos y submarinos",
    de: "Prozessdaten für Öl & Gas, Chemie & Subsea",
    fr: "Données procédés pétrole et gaz, chimie et sous-marins",
    pt: "Dados para processos de óleo e gás, químicos e submarinos"
  },
  detailUrl: "/CorrosionResistanceMatricesPage"
}, {
  id: "doc-4",
  code: "CALC-PRESS-02",
  category: {
    en: "Process & SOP",
    zh: "工艺与工程计算",
    es: "Procesos y cálculo",
    de: "Verfahren & Berechnung",
    fr: "Procédés et calculs",
    pt: "Processos e cálculo"
  },
  title: {
    en: "Multilayer Sintered Mesh Differential Pressure Calculation",
    zh: "多层金属烧结网流体阻力与工作压降核算指南",
    es: "Cálculo de presión diferencial para malla sinterizada multicapa",
    de: "Differenzdruckberechnung für mehrlagiges Sintergewebe",
    fr: "Calcul de perte de charge pour toiles frittées multicouches",
    pt: "Cálculo de pressão diferencial para tela sinterizada multicamada"
  },
  summary: {
    en: "Flow rate modeling, permeability coefficient calculations, dirt holding capacity, and maximum allowable differential pressure guidelines for 5-layer mesh.",
    zh: "涵盖 5 层标准烧结网的流体流量建模、透气/透液渗透系数计算、纳污容量及最大允许运行压差核算公式。",
    es: "Modelado de caudal, coeficiente de permeabilidad, capacidad de retención de suciedad y presión diferencial máxima admisible.",
    de: "Durchflussmodellierung, Permeabilitätskoeffizienten, Schmutzaufnahmekapazität und maximal zulässiger Differenzdruck für 5-Lagen-Gewebe.",
    fr: "Modélisation des débits, coefficients de perméabilité, capacité de rétention et perte de charge admissible pour toiles à 5 couches.",
    pt: "Modelagem de vazão, coeficientes de permeabilidade, capacidade de retenção de sujeira e pressão diferencial admissível para telas de 5 camadas."
  },
  specs: {
    en: "Flow Velocity vs. ΔP Engineering Formulas",
    zh: "流速与工作压差工程水力学公式",
    es: "Fórmulas de ingeniería de velocidad de flujo frente a ΔP",
    de: "Formeln für Strömungsgeschwindigkeit vs. ΔP",
    fr: "Formules d'ingénierie vitesse d'écoulement vs ΔP",
    pt: "Fórmulas de engenharia de velocidade de fluxo vs ΔP"
  },
  detailUrl: "/CalculationGuidesPage"
}];
const copy = {
  eyebrow: {
    en: "Technical Support & Specifications",
    zh: "技术支持与规格文档",
    es: "Soporte técnico y especificaciones",
    de: "Technische Unterstützung & Spezifikationen",
    fr: "Support technique et spécifications",
    pt: "Suporte técnico e especificações"
  },
  h2: {
    en: "Technical resources & engineering datasheets",
    zh: "技术资料与工程数据规格书",
    es: "Recursos técnicos y fichas de ingeniería",
    de: "Technische Ressourcen & Engineering-Datenblätter",
    fr: "Ressources techniques et fiches d'ingénierie",
    pt: "Recursos técnicos e fichas de engenharia"
  },
  lead: {
    en: "Access verified technical data, chemical compatibility matrices, mesh calculation tools, and compliance frameworks for critical industrial filtration media.",
    zh: "获取经过工程验证的技术数据、化学兼容性矩阵、丝网计算工具以及关键工业过滤介质的合规标准框架。",
    es: "Acceda a datos técnicos verificados, matrices de compatibilidad química, herramientas de cálculo de malla y marcos de cumplimiento para medios filtrantes críticos.",
    de: "Nutzen Sie verifizierte technische Daten, chemische Beständigkeitsmatrizen, Berechnungstools und Konformitätsrichtlinien für anspruchsvolle Filtermedien.",
    fr: "Accédez à des données techniques vérifiées, matrices de compatibilité chimique, outils de calcul et référentiels de conformité pour médias filtrants industriels.",
    pt: "Acesse dados técnicos verificados, matrizes de compatibilidade química, ferramentas de cálculo de malha e estruturas de conformidade para meios filtrantes críticos."
  },
  viewDatasheet: {
    en: "View Datasheet",
    zh: "查阅规格书",
    es: "Ver ficha técnica",
    de: "Datenblatt ansehen",
    fr: "Voir la fiche technique",
    pt: "Ver ficha técnica"
  },
  requestReview: {
    en: "Request engineering review",
    zh: "申请工程复核",
    es: "Solicitar revisión técnica",
    de: "Technische Prüfung anfragen",
    fr: "Demander une revue technique",
    pt: "Solicitar revisão técnica"
  },
  quickLinks: {
    datasheets: {
      title: {
        en: "Product Datasheets",
        zh: "产品规格表",
        es: "Fichas de producto",
        de: "Produktdatenblätter",
        fr: "Fiches produits",
        pt: "Fichas de produto"
      },
      desc: {
        en: "Full mesh aperture & wire size specs",
        zh: "完整孔径公差与丝径规格",
        es: "Especificaciones completas de apertura y alambre",
        de: "Vollständige Maschenweiten- & Drahtspezifikationen",
        fr: "Spécifications complètes d'ouvertures et fils",
        pt: "Especificações completas de abertura e arames"
      }
    },
    calculations: {
      title: {
        en: "Calculation Guides",
        zh: "工程计算指南",
        es: "Guías de cálculo",
        de: "Berechnungsleitfäden",
        fr: "Guides de calcul",
        pt: "Guias de cálculo"
      },
      desc: {
        en: "Pressure drop & open area formulas",
        zh: "压降模型与开孔率计算公式",
        es: "Fórmulas de caída de presión y área abierta",
        de: "Formeln für Druckverlust & offene Siebfläche",
        fr: "Formules de perte de charge et surface ouverte",
        pt: "Fórmulas de perda de carga e área aberta"
      }
    },
    standards: {
      title: {
        en: "ISO & ASTM Standards",
        zh: "ISO 与 ASTM 行业规范",
        es: "Normas ISO y ASTM",
        de: "ISO- & ASTM-Normen",
        fr: "Normes ISO et ASTM",
        pt: "Normas ISO e ASTM"
      },
      desc: {
        en: "Quality tolerances & inspection",
        zh: "质量公差范围与检验规程",
        es: "Tolerancias de calidad e inspección",
        de: "Qualitätstoleranzen & Prüfung",
        fr: "Tolérances de qualité et inspection",
        pt: "Tolerâncias de qualidade e inspeção"
      }
    },
    corrosion: {
      title: {
        en: "Corrosion Matrices",
        zh: "耐腐蚀材质矩阵",
        es: "Matrices de corrosión",
        de: "Korrosionsmatrizen",
        fr: "Matrices de corrosion",
        pt: "Matrizes de corrosão"
      },
      desc: {
        en: "Alloy selection for corrosive media",
        zh: "腐蚀性介质合金选材推荐",
        es: "Selección de aleaciones para fluidos agresivos",
        de: "Legierungsauswahl für korrosive Medien",
        fr: "Sélection d'alliages pour milieux corrosifs",
        pt: "Seleção de ligas para meios agressivos"
      }
    }
  },
  bannerTitle: {
    en: "Need custom mesh calculations or bespoke filter engineering?",
    zh: "需要定制丝网核算或专属过滤工程结构设计？",
    es: "¿Necesita cálculos de malla a medida o ingeniería de filtración personalizada?",
    de: "Benötigen Sie individuelle Gewebeberechnungen oder maßgeschneiderte Filtertechnik?",
    fr: "Besoin de calculs de toiles sur mesure ou d'ingénierie de filtration dédiée ?",
    pt: "Precisa de cálculos de tela sob medida ou engenharia de filtração personalizada?"
  },
  bannerBody: {
    en: "Our metallurgical and filtration application engineers assist with custom aperture sizing, multi-layer sintered pack design, alloy verification, and pressure drop optimization.",
    zh: "我们的冶金材料与过滤应用工程师协助您完成特殊孔径选型、多层烧结网结构设计、合金材质确认及运行压降优化。",
    es: "Nuestros ingenieros metalúrgicos y de filtración le asisten con el dimensionamiento de aperturas, diseño de paquetes sinterizados, verificación de aleaciones y optimización de caídas de presión.",
    de: "Unsere Werkstoff- und Anwendungstechniker unterstützen Sie bei Porenweitenbestimmung, Auslegung gesinterter Verbundpakete, Werkstoffprüfung und Druckverlustoptimierung.",
    fr: "Nos ingénieurs en métallurgie et filtration vous accompagnent pour le dimensionnement des ouvertures, la conception de packs frittés, la validation des alliages et l'optimisation des pertes de charge.",
    pt: "Nossos engenheiros metalúrgicos e de filtração auxiliam no dimensionamento de aberturas, projeto de pacotes sinterizados, verificação de ligas e otimização de perda de carga."
  }
};
function TechnicalDocsConsumer() {
  const {
    lang
  } = useLanguage();
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[...Array(4)].map((_, i) => <div key={i} className="h-64 rounded-lg bg-muted/40 border border-border" />)}
      </div>;
  }
  const itemsToRender = posts && posts.length > 0 ? posts.map(post => ({
    id: post.id,
    code: post.customFields?.doc_code || "TECH-REF",
    category: post.customFields?.category || "Product Reference",
    title: post.title,
    summary: post.customFields?.summary || post.excerpt || "Technical documentation and specification guide.",
    specs: post.customFields?.specs_highlight || "Standard Engineering Spec",
    detailUrl: post.link || "/DatasheetsPage"
  })) : STATIC_DOCUMENTS.map(doc => ({
    id: doc.id,
    code: doc.code,
    category: pick(lang, doc.category),
    title: pick(lang, doc.title),
    summary: pick(lang, doc.summary),
    specs: pick(lang, doc.specs),
    detailUrl: doc.detailUrl
  }));
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {itemsToRender.map((doc, i) => <Reveal key={doc.id} delay={i * 60}>
          <Card data-index={i} className="flex flex-col justify-between border-border bg-card rounded-lg h-full">
            <CardHeader className="space-y-3 pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Badge variant="outline" className="bg-muted text-foreground font-medium mf-caption border border-border">
                  {doc.category}
                </Badge>
                <span className="font-mono mf-caption font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-sm">
                  {doc.code}
                </span>
              </div>
              <CardTitle className="text-lg font-semibold tracking-tight text-card-foreground leading-snug">
                {doc.title}
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4 flex-1">
              <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                {doc.summary}
              </CardDescription>

              <div className="flex items-center gap-2 text-sm font-medium text-foreground bg-muted/60 border border-border/60 rounded-md p-2.5">
                <FileCheck className="size-4 shrink-0 text-primary" />
                <span>{doc.specs}</span>
              </div>
            </CardContent>

            <CardFooter className="pt-2 border-t border-border/50 flex flex-wrap items-center justify-between gap-3">
              <Link to={doc.detailUrl} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                <FileText className="size-3.5" />
                <span>{pick(lang, copy.viewDatasheet)}</span>
              </Link>

              <Button asChild variant="outline" size="sm">
                <Link to="/RequestAQuotePage">
                  {pick(lang, copy.requestReview)}
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </Reveal>)}
    </div>;
}
export default function TechnicalResources() {
  const {
    lang
  } = useLanguage();
  return <section id="technicalresources" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container space-y-12">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 mf-caption font-bold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-md">
              <ShieldCheck className="size-4" />
              <span>{pick(lang, copy.eyebrow)}</span>
            </div>

            <h2 className="mf-h2 text-foreground font-sans">
              {pick(lang, copy.h2)}
            </h2>

            <p className="mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Dynamic Technical Documents Grid */}
        <PostsProvider wp_query={{
        post_type: "technical_document",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <TechnicalDocsConsumer />
        </PostsProvider>

        {/* Quick Reference Links Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
          <Reveal delay={0}>
            <Link to="/DatasheetsPage" className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors h-full">
              <div className="p-2 rounded-md bg-muted text-primary border border-border shrink-0">
                <FileText className="size-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {pick(lang, copy.quickLinks.datasheets.title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-0.5">
                  {pick(lang, copy.quickLinks.datasheets.desc)}
                </p>
              </div>
            </Link>
          </Reveal>

          <Reveal delay={60}>
            <Link to="/CalculationGuidesPage" className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors h-full">
              <div className="p-2 rounded-md bg-muted text-primary border border-border shrink-0">
                <Settings className="size-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {pick(lang, copy.quickLinks.calculations.title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-0.5">
                  {pick(lang, copy.quickLinks.calculations.desc)}
                </p>
              </div>
            </Link>
          </Reveal>

          <Reveal delay={120}>
            <Link to="/ISOWireMeshStandardsPage" className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors h-full">
              <div className="p-2 rounded-md bg-muted text-primary border border-border shrink-0">
                <CheckCircle2 className="size-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {pick(lang, copy.quickLinks.standards.title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-0.5">
                  {pick(lang, copy.quickLinks.standards.desc)}
                </p>
              </div>
            </Link>
          </Reveal>

          <Reveal delay={180}>
            <Link to="/CorrosionResistanceMatricesPage" className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors h-full">
              <div className="p-2 rounded-md bg-muted text-primary border border-border shrink-0">
                <Layers className="size-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {pick(lang, copy.quickLinks.corrosion.title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-0.5">
                  {pick(lang, copy.quickLinks.corrosion.desc)}
                </p>
              </div>
            </Link>
          </Reveal>
        </div>

        {/* Engineering Review CTA Banner */}
        <Reveal delay={200}>
          <div className="rounded-lg bg-surface-dark text-surface-dark-foreground p-6 sm:p-8 border border-surface-dark-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <h3 className="mf-h3 text-surface-dark-foreground">
                {pick(lang, copy.bannerTitle)}
              </h3>
              <p className="text-sm text-surface-dark-muted leading-relaxed">
                {pick(lang, copy.bannerBody)}
              </p>
            </div>

            <Button asChild size="lg" className="shrink-0 font-medium">
              <Link to="/RequestAQuotePage">
                {pick(lang, copy.requestReview)}
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>;
}